'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, ArrowLeft, CheckCircle, ShieldCheck } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

// Minimum ms between login attempts to avoid hammering Supabase auth
const LOGIN_THROTTLE_MS = 2000;

// Roles that require TOTP 2FA
const TOTP_REQUIRED_ROLES = ['super_admin', 'ops_admin'];

async function resolveRoleFromProfile(userId: string, userEmail: string): Promise<string> {
  let supabase = createClient();
  try {
    const adminEmails = (process.env.NEXT_PUBLIC_ADMIN_EMAILS || '')
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

    const { data } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    if (data?.role) {
      if (['teacher_checkin', 'ops_admin', 'super_admin'].includes(data.role)) {
        return data.role;
      }
      if (data.role === 'admin' || adminEmails.includes(userEmail.toLowerCase())) {
        return 'super_admin';
      }
      return data.role;
    }

    if (adminEmails.includes(userEmail.toLowerCase())) return 'super_admin';
    return 'family';
  } catch {
    return 'family';
  }
}

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const lastSubmitRef = React.useRef<number>(0);

  // Detect missing env vars on mount
  const [configError, setConfigError] = React.useState('');
  React.useEffect(() => {
    if (!process.env.NEXT_PUBLIC_SUPABASE_URL || !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY) {
      setConfigError('Authentication service is not configured. Please contact support.');
    }
  }, []);

  // TOTP state
  const [totpStep, setTotpStep] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpSubmitting, setTotpSubmitting] = useState(false);
  const [totpError, setTotpError] = useState('');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<string | null>(null);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  function redirectByRole(r: string) {
    let dest = '/client-portal';
    if (r === 'super_admin' || r === 'admin') {
      dest = '/admin-operations-dashboard';
    } else if (r === 'ops_admin') {
      dest = '/makeup-scheduling';
    } else if (r === 'teacher_checkin') {
      dest = '/teacher-check-in-dashboard';
    } else if (r === 'teacher') {
      dest = '/teacher-portal';
    }
    // Use window.location for a hard navigation — guarantees the redirect fires
    // even if the Next.js router is in a transitional state after sign-in.
    window.location.href = dest;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const now = Date.now();
    if (now - lastSubmitRef.current < LOGIN_THROTTLE_MS) return;
    lastSubmitRef.current = now;

    setError('');
    setSubmitting(true);

    try {
      let supabase;
      try {
        supabase = createClient();
      } catch (clientErr: any) {
        throw new Error('Authentication service unavailable. Please contact support. (' + (clientErr?.message || 'config error') + ')');
      }
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;

      const user = data?.user;
      if (!user) throw new Error('Sign in failed. Please try again.');

      // Resolve role directly — no dependency on AuthContext state
      const role = await resolveRoleFromProfile(user.id, user.email ?? '');

      // Check if this role requires TOTP
      if (TOTP_REQUIRED_ROLES.includes(role)) {
        try {
          const res = await fetch(`/api/totp/verify?userId=${user.id}`);
          const totpData = await res.json();
          if (totpData.totpRequired) {
            setPendingUserId(user.id);
            setPendingRole(role);
            setTotpStep(true);
            setSubmitting(false);
            return;
          }
        } catch {
          // If TOTP check fails, proceed without it
        }
      }

      redirectByRole(role);
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
      setSubmitting(false);
    }
  };

  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingUserId || !totpCode.trim()) return;

    setTotpError('');
    setTotpSubmitting(true);

    try {
      const res = await fetch('/api/totp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pendingUserId, code: totpCode.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setTotpError(data.error || 'Invalid code. Please try again.');
        setTotpSubmitting(false);
        return;
      }

      // TOTP verified — redirect
      redirectByRole(pendingRole ?? 'admin');
    } catch {
      setTotpError('Verification failed. Please try again.');
      setTotpSubmitting(false);
    }
  };

  const handleCancelTotp = async () => {
    // Sign out and reset state
    try {
      let supabase = createClient();
      await supabase.auth.signOut();
    } catch { /* ignore */ }
    setTotpStep(false);
    setTotpCode('');
    setTotpError('');
    setPendingUserId(null);
    setPendingRole(null);
    setSubmitting(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      let supabase;
      try {
        supabase = createClient();
      } catch (clientErr: any) {
        throw new Error('Authentication service unavailable. (' + (clientErr?.message || 'config error') + ')');
      }
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `${siteUrl}/auth/confirm`,
      });
      if (error) throw error;
      setForgotSent(true);
    } catch (err: any) {
      setForgotError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8" style={{ background: 'linear-gradient(135deg, #0d4f5c 0%, #0f766e 35%, #134e6f 70%, #0f172a 100%)' }}>
      <div className="w-full max-w-md">

        {/* Logo above card */}
        <div className="flex justify-center mb-6">
          <AppLogo size="lg" variant="full" />
        </div>

        {configError && (
          <div className="mb-4 bg-red-500/15 border border-red-500/30 rounded-xl px-4 py-3">
            <p className="text-red-300 text-sm text-center">{configError}</p>
          </div>
        )}

        {/* Card */}
        <div className="bg-white/8 border border-white/15 rounded-2xl p-6 sm:p-8 backdrop-blur-sm">

          {totpStep ? (
            /* ── TOTP Verification Panel ── */
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={20} className="text-teal-300" />
                </div>
                <div>
                  <h2 className="text-white text-lg font-semibold">Two-Factor Verification</h2>
                  <p className="text-teal-200/70 text-xs">Enter the 6-digit code from your authenticator app</p>
                </div>
              </div>

              <form onSubmit={handleTotpVerify} className="space-y-4">
                <div>
                  <label htmlFor="totpCode" className="block text-sm font-medium text-teal-100 mb-1.5">
                    Verification Code
                  </label>
                  <input
                    id="totpCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full px-3.5 py-3 bg-white/5 border border-white/15 rounded-lg text-white placeholder-teal-300/40 text-xl font-mono tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-150"
                  />
                </div>

                {totpError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3.5 py-2.5">
                    <p className="text-red-400 text-sm">{totpError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={totpSubmitting || totpCode.length !== 6}
                  className="w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium text-sm py-2.5 rounded-lg transition-all duration-150"
                  style={{ background: 'linear-gradient(90deg, #0f766e, #134e6f)' }}
                >
                  {totpSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={16} />
                      Verify &amp; Sign In
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCancelTotp}
                  className="w-full flex items-center justify-center gap-1.5 text-teal-300/70 hover:text-teal-100 text-sm transition-colors py-1"
                >
                  <ArrowLeft size={14} /> Back to sign in
                </button>
              </form>

              <p className="text-teal-300/40 text-xs text-center mt-5">
                Open your authenticator app (Google Authenticator, Authy, etc.) to find your code.
              </p>
            </div>
          ) : forgotMode ? (
            /* ── Forgot Password Panel ── */
            forgotSent ? (
              <div className="flex flex-col items-center text-center gap-4 py-4">
                <CheckCircle size={48} className="text-teal-400" />
                <h2 className="text-white text-xl font-semibold">Check your email</h2>
                <p className="text-teal-200/70 text-sm">
                  We sent a password reset link to <span className="text-teal-200 font-medium">{forgotEmail}</span>. Click the link in the email to set a new password.
                </p>
                <button
                  onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); }}
                  className="text-teal-300 text-sm hover:text-white transition-colors mt-2"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { setForgotMode(false); setForgotError(''); }}
                  className="flex items-center gap-1.5 text-teal-300/70 hover:text-teal-100 text-sm mb-5 transition-colors"
                >
                  <ArrowLeft size={14} /> Back to sign in
                </button>
                <h2 className="text-white text-xl font-semibold mb-1">Reset your password</h2>
                <p className="text-teal-200/70 text-sm mb-6">Enter your email and we'll send you a reset link.</p>

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label htmlFor="forgotEmail" className="block text-sm font-medium text-teal-100 mb-1.5">
                      Email address
                    </label>
                    <input
                      id="forgotEmail"
                      type="email"
                      autoComplete="email"
                      required
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-3.5 py-2.5 bg-white/5 border border-white/15 rounded-lg text-white placeholder-teal-300/40 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-150"
                    />
                  </div>

                  {forgotError && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3.5 py-2.5">
                      <p className="text-red-400 text-sm">{forgotError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium text-sm py-2.5 rounded-lg transition-all duration-150 mt-2"
                    style={{ background: 'linear-gradient(90deg, #0f766e, #134e6f)' }}
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </button>
                </form>
              </>
            )
          ) : (
            /* ── Sign In Panel ── */
            <>
              <h2 className="text-white text-xl font-semibold mb-1">Sign in to your account</h2>
              <p className="text-teal-200/70 text-sm mb-6">Enter your credentials to access your portal</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-teal-100 mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3.5 py-2.5 bg-white/5 border border-white/15 rounded-lg text-white placeholder-teal-300/40 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-150"
                  />
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-teal-100">
                      Password
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-3.5 py-2.5 pr-10 bg-white/5 border border-white/15 rounded-lg text-white placeholder-teal-300/40 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-150"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-300/60 hover:text-teal-100 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3.5 py-2.5">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium text-sm py-2.5 rounded-lg transition-all duration-150 mt-2"
                  style={{ background: 'linear-gradient(90deg, #0f766e, #134e6f)' }}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>

              {/* Forgot password — outside form, always visible */}
              <div className="mt-5 pt-4 border-t border-white/10 text-center">
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setForgotEmail(email); }}
                  className="text-base font-medium text-white bg-white/10 hover:bg-white/20 transition-colors px-6 py-2 rounded-lg border border-white/20"
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-teal-300/40 text-xs mt-6">
          © 2026 California Fine &amp; Visual Arts · Internal Operations Platform
        </p>
      </div>
    </div>
  );
}
